import { v4 as uuidv4 } from 'uuid'
import { Device } from '@twilio/voice-sdk'
import dayjs from 'dayjs'
import { defineStore } from 'pinia'
import { useApi } from '~/composables/useApi'

let device = null
let connectionRegistry = {}

const CONNECTION_STATUS = {
  PENDING: 'PENDING',
  RINGING: 'RINGING',
  IN_PROGRESS: 'IN_PROGRESS',
  IGNORED: 'IGNORED',
  REJECTED: 'REJECTED',
  FINISHED: 'FINISHED'
}

export const useCallConsoleStore = defineStore('call-console', () => {
  const { api } = useApi()
  const state = ref({
    consoleStatus: 'Offline',
    consoleStatusDebug: '',
    callStack: [],
    token: null,
    tokenRefreshTime: null
  })

  const getConsoleStatus = computed(() => {
    return state.value.consoleStatus
  })

  const isConsoleReadyForCalls = computed(() => {
    return activeCalls.value.length === 0 && getConsoleStatus.value === 'Online'
  })

  const isInCall = computed(() => {
    return activeCalls.value.length > 0
  })

  const availableInputDevices = computed(() => {
    return state.value.inputDevices
  })

  const activeCalls = computed(() => {
    const finalStatuses = [
      CONNECTION_STATUS.IGNORED,
      CONNECTION_STATUS.REJECTED,
      CONNECTION_STATUS.FINISHED
    ]

    return state.value.callStack.filter(call => !finalStatuses.includes(call.status))
  })

  const allCalls = computed(() => {
    return state.value.callStack
  })
  function resetConsole () {
    device = null
    connectionRegistry = {}
    state.value.consoleStatus = 'Offline'
    state.value.consoleStatusDebug = ''
    state.value.callStack = []
    state.value.token = null
    state.value.tokenRefreshTime = null
  }
  function setAuthToken (token) {
    state.value.token = token
    state.value.tokenRefreshTime = dayjs().add(55, 'minutes')
    if (device) {
      device.updateToken(token)
    }
  }
  function updateConsoleStatus ({ status, debugInfo }) {
    state.value.consoleStatus = status
    state.value.consoleStatusDebug = debugInfo
  }
  function updateConnectionStatus ({ id, status }) {
    const call = state.value.callStack.find(call => call.id === id)

    if (call) {
      call.status = status
    }
  }
  function updateConnectionCallSid ({ id, connection }) {
    const call = state.value.callStack.find(call => call.id === id)
    const callSid = connection.parameters.CallSid

    if (call && call.callSid === undefined) {
      call.callSid = callSid
    }
  }
  function updateCallFinalStatus ({ id, finalStatus }) {
    const call = state.value.callStack.find(call => call.id === id)
    call.finalStatus = finalStatus
  }
  function updateConnectionMuted ({ id, isMuted }) {
    const call = state.value.callStack.find(call => call.id === id)

    if (call) {
      call.muted = isMuted
    }
  }
  function addCallToStack (call) {
    state.value.callStack.push(call)
  }
  function initialiseDevice () {
    if (device !== null) {
      return
    }

    updateToken().then(() => {
      device = new Device(state.value.token)
      device.register()

      device.on('registered', (_device) => {
        updateConsoleStatus({ status: 'Online' })
      })

      device.on('unregistered', (_device) => {
        updateConsoleStatus({ status: 'Offline' })
      })

      /* Report any errors to the call status display */
      device.on('error', (error) => {
        updateConsoleStatus({ status: 'Error', debugInfo: error.message })
      })

      /* Callback for when Twilio Client initiates a new connection */
      device.on('connect', (connection) => {
        updateConsoleStatus({ status: 'In call' })
      })

      /* Callback for when a call ends */
      device.on('disconnect', (connection) => {
        updateConsoleStatus({ status: 'Online' })
      })

      /* Callback for when Twilio Client receives a new incoming call */
      device.on('incoming', (connection) => {
        initialiseIncoming({ connection })
      })

      // setup loop to refresh token before expiry
      setInterval(() => { updateToken() }, 60000)
    }).catch(() => {
      // Token update failed
    })
  }

  async function updateToken () {
    if (state.value.token !== null && dayjs().isBefore(state.value.tokenRefreshTime)) {
      return
    }

    try {
      const response = await api('/partner/twilio/token', {
        method: 'POST'
      })
      const token = response.token
      setAuthToken(token)
    } catch (error) {
      updateConsoleStatus({ status: 'Error', debugInfo: 'Could not get a token from server!' })
      throw error
    }
  }

  async function initialiseOutgoing ({ customerId, phoneNumber }) {
    if (!isConsoleReadyForCalls.value) {
      return
    }

    const connection = await device.connect({
      params: {
        phoneNumber,
        ...(customerId ? { customerId } : {})
      }
    })

    const id = uuidv4()
    connectionRegistry[id] = connection

    const call = {
      id,
      type: 'outgoing',
      status: CONNECTION_STATUS.PENDING,
      muted: false,
      callSid: undefined,
      finalStatus: undefined,
      customerName: '-',
      ...(customerId ? { customerId } : {}),
      phoneNumber
    }

    attachConnectionListeners({ connection, id })
    addCallToStack(call)
  }
  function initialiseIncoming ({ connection }) {
    const id = uuidv4()
    connectionRegistry[id] = connection

    const call = {
      id,
      type: 'incoming',
      status: CONNECTION_STATUS.PENDING,
      muted: false,
      callSid: connection.customParameters.get('parentCallSid'),
      finalStatus: undefined,
      customerName: connection.customParameters.get('fullName'),
      customerId: connection.customParameters.get('customerId'),
      phoneNumber: connection.parameters.From
    }

    attachConnectionListeners({ connection, id })
    addCallToStack(call)
  }
  function attachConnectionListeners ({ connection, id }) {
    connection.on('accept', (connection) => {
      updateConnectionStatus({ id, status: CONNECTION_STATUS.IN_PROGRESS })
      updateConnectionCallSid({ id, connection })
    })
    connection.on('reject', () => {
      updateConnectionStatus({ id, status: CONNECTION_STATUS.REJECTED })
    })
    connection.on('cancel', () => {
      updateConnectionStatus({ id, status: CONNECTION_STATUS.IGNORED })
    })
    connection.on('disconnect', (connection) => {
      updateConnectionStatus({ id, status: CONNECTION_STATUS.FINISHED })
      updateConnectionCallSid({ id, connection })
      updateLastCallTwilioFinalStatus({ id })
    })
    connection.on('ringing', (hasEarlyMedia) => {
      updateConnectionStatus({ id, status: CONNECTION_STATUS.RINGING })
    })

    connection.on('mute', (isMuted, connection) => {
      updateConnectionMuted({ id, isMuted })
    })
  }
  function acceptCall ({ id }) {
    const connection = connectionRegistry[id]

    if (connection) {
      connection.accept()
    }
  }
  function rejectCall ({ id }) {
    const connection = connectionRegistry[id]

    if (connection) {
      connection.reject()
    }
  }
  function ignoreCall ({ id }) {
    const connection = connectionRegistry[id]

    if (connection) {
      connection.ignore()
    }
  }
  function disconnectCall ({ id }) {
    const connection = connectionRegistry[id]

    if (connection) {
      connection.disconnect()
    }
  }
  function toggleMute ({ id }) {
    const connection = connectionRegistry[id]

    if (connection) {
      connection.mute(!connection.isMuted())
    }
  }
  function updateLastCallTwilioFinalStatus ({ id }) {
    const call = state.value.callStack.find(call => call.id === id)

    if (!call.callSid) {
      return
    }

    const finalStatuses = [
      'COMPLETED',
      'BUSY',
      'NO-ANSWER',
      'CANCELLED',
      'FAILED'
    ]

    let attempt = 1

    const poolStatus = setInterval(async () => {
      const url = `/calls/${call.callSid}`
      try {
        const response = await api(url, {
          method: 'GET'
        })
        const finalStatus = response.status
        if (finalStatuses.includes(finalStatus)) {
          clearInterval(poolStatus)
          updateCallFinalStatus({ id, finalStatus })
        }
      } catch (error) {
        // Handle error if needed
      } finally {
        attempt = attempt + 1
        if (attempt > 5) {
          clearInterval(poolStatus)
        }
      }
    }, 1000)
  }

  return {
    state,
    getConsoleStatus,
    isConsoleReadyForCalls,
    isInCall,
    availableInputDevices,
    activeCalls,
    allCalls,
    resetConsole,
    setAuthToken,
    updateConsoleStatus,
    updateConnectionStatus,
    updateConnectionCallSid,
    updateCallFinalStatus,
    updateConnectionMuted,
    addCallToStack,
    initialiseDevice,
    updateToken,
    initialiseOutgoing,
    initialiseIncoming,
    attachConnectionListeners,
    acceptCall,
    rejectCall,
    ignoreCall,
    disconnectCall,
    toggleMute,
    updateLastCallTwilioFinalStatus
  }
})
